/**
 * Lyra storefront analytics data layer.
 *
 * This module publishes versioned `lyra_*` messages only. It does not call
 * gtag or GA4 directly. GTM mappings must stay disabled until the event-owner
 * and canonical item_id audit is complete.
 */
(function lyraAnalyticsBootstrap() {
  'use strict';

  const contextElement = document.getElementById('lyra-analytics-context');

  if (!contextElement || window.LyraAnalytics?.initialized) return;

  let context;

  try {
    context = JSON.parse(contextElement.textContent);
  } catch (error) {
    return;
  }

  const SCHEMA_VERSION = context.schema_version || '1.0';
  const IMPRESSION_RATIO = 0.5;
  const IMPRESSION_DURATION_MS = 500;
  const COLLECTION_CARD_SELECTOR = '.collection__main product-list [data-lyra-product-card]';
  const CTA_CONTROL_SELECTOR = '[data-lyra-cta-source] button[type="submit"], [data-lyra-cta-source] button[data-lyra-button-state]';
  const debugEnabled = new URLSearchParams(window.location.search).get('lyra_analytics_debug') === '1';
  const dedupeKeys = new Set();
  const pendingAttempts = new WeakMap();
  const observedCards = new WeakSet();
  const observedCtas = new WeakSet();
  const impressionTimers = new WeakMap();
  const ctaTimers = new WeakMap();
  const cardRatios = new WeakMap();
  const ctaRatios = new WeakMap();
  const seenImpressions = new Set();
  const queuedImpressions = new Map();
  let impressionFlushTimer = null;
  let pendingFacetAction = null;
  let facetActionFlushTimer = null;
  let lastCollectionSort = context.collection?.sort_by;

  window.dataLayer = window.dataLayer || [];

  function createId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();

    return `lyra-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function cleanObject(value) {
    if (Array.isArray(value)) return value.map(cleanObject);
    if (!value || typeof value !== 'object') return value;

    return Object.entries(value).reduce((result, [key, entry]) => {
      if (entry === undefined || entry === null || entry === '') return result;
      result[key] = cleanObject(entry);
      return result;
    }, {});
  }

  function push(eventName, payload = {}, options = {}) {
    const fullEventName = eventName.startsWith('lyra_') ? eventName : `lyra_${eventName}`;

    if (options.dedupeKey) {
      if (dedupeKeys.has(options.dedupeKey)) return null;
      dedupeKeys.add(options.dedupeKey);
    }

    if (payload.ecommerce) window.dataLayer.push({ ecommerce: null });

    const message = cleanObject({
      event: fullEventName,
      schema_version: SCHEMA_VERSION,
      event_id: options.eventId || createId(),
      page: context.page,
      ...payload
    });

    window.dataLayer.push(message);

    if (debugEnabled && window.console) console.info('[Lyra analytics]', JSON.stringify(message));

    return message;
  }

  function numberFromMinorUnits(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed / 100 : undefined;
  }

  function totalValue(priceMinorUnits, quantity) {
    const price = numberFromMinorUnits(priceMinorUnits);
    return typeof price === 'number' ? price * quantity : undefined;
  }

  function canonicalItemId(productId, variantId) {
    const prefix = context.shop?.item_id_prefix;
    if (!prefix || !productId || !variantId) return undefined;
    return `${prefix}_${productId}_${variantId}`;
  }

  function currentListSignature() {
    const url = new URL(window.location.href);
    const parameters = new URLSearchParams(url.search);
    parameters.sort();
    return `${url.pathname}?${parameters.toString()}`;
  }

  function currentCollection() {
    const surface = document.querySelector('[data-lyra-collection-result-count]');
    const resultCount = Number(surface?.dataset.lyraCollectionResultCount);

    return cleanObject({
      ...context.collection,
      result_count: Number.isFinite(resultCount) ? resultCount : context.collection?.result_count,
      sort_by: surface?.dataset.lyraCollectionSort || context.collection?.sort_by
    });
  }

  function filterState(parameters) {
    const valuesByName = new Map();

    parameters.forEach((value, name) => {
      if (!name.startsWith('filter.') || value === '') return;
      if (!valuesByName.has(name)) valuesByName.set(name, []);
      valuesByName.get(name).push(value);
    });

    valuesByName.forEach((values) => values.sort());
    return valuesByName;
  }

  function changedFilterNames(previousState, nextState) {
    const names = new Set([...previousState.keys(), ...nextState.keys()]);

    return Array.from(names)
      .filter((name) => JSON.stringify(previousState.get(name) || []) !== JSON.stringify(nextState.get(name) || []))
      .map((name) => name.replace(/^filter\./, ''))
      .sort();
  }

  function selectedFilterValueCount(state) {
    return Array.from(state.values()).reduce((total, values) => total + values.length, 0);
  }

  function flushFacetAction() {
    facetActionFlushTimer = null;
    const collection = currentCollection();
    const action = pendingFacetAction;
    pendingFacetAction = null;

    if (action) {
      push(action.eventName, {
        collection,
        interaction: action.interaction
      });
    } else if (collection.sort_by && collection.sort_by !== lastCollectionSort) {
      push('collection_sort_apply', {
        collection,
        interaction: {
          previous_sort: lastCollectionSort,
          sort_by: collection.sort_by
        }
      });
    }

    lastCollectionSort = collection.sort_by || lastCollectionSort;
  }

  document.addEventListener('facet:update', (event) => {
    if (context.page.type !== 'collection' || !event.detail?.url) return;

    const previousUrl = new URL(window.location.href);
    const nextUrl = new URL(event.detail.url.toString(), window.location.href);
    const previousFilters = filterState(previousUrl.searchParams);
    const nextFilters = filterState(nextUrl.searchParams);
    const filterNames = changedFilterNames(previousFilters, nextFilters);

    if (filterNames.length) {
      pendingFacetAction = {
        eventName: 'collection_filter_apply',
        interaction: {
          filter_names: filterNames,
          previous_selected_value_count: selectedFilterValueCount(previousFilters),
          selected_value_count: selectedFilterValueCount(nextFilters)
        }
      };
      return;
    }

    const previousSort = previousUrl.searchParams.get('sort_by') || context.collection?.sort_by;
    const nextSort = nextUrl.searchParams.get('sort_by') || context.collection?.sort_by;

    if (previousSort !== nextSort) {
      pendingFacetAction = {
        eventName: 'collection_sort_apply',
        interaction: {
          previous_sort: previousSort,
          sort_by: nextSort
        }
      };
    }
  }, true);

  document.addEventListener('theme:loading:end', () => {
    if (context.page.type !== 'collection' || facetActionFlushTimer) return;

    window.requestAnimationFrame(() => {
      if (facetActionFlushTimer) return;
      facetActionFlushTimer = window.setTimeout(flushFacetAction, 0);
    });
  });

  function cardIndex(card) {
    const list = card.closest('product-list');
    if (!list) return undefined;

    const renderedSlots = Array.from(list.children).filter((element) => {
      const rect = element.getBoundingClientRect();
      return !element.hidden && element.getClientRects().length > 0 && rect.width > 0 && rect.height > 0;
    });
    const index = renderedSlots.indexOf(card);
    return index >= 0 ? index + 1 : undefined;
  }

  function itemFromCard(card) {
    const itemListId = card.dataset.lyraItemListId || context.collection?.id;
    const itemListName = card.dataset.lyraItemListName || context.collection?.name;
    const shopifyProductId = card.dataset.lyraShopifyProductId;
    const shopifyVariantId = card.dataset.lyraShopifyVariantId;

    return cleanObject({
      item_id: canonicalItemId(shopifyProductId, shopifyVariantId),
      item_name: card.dataset.lyraItemName,
      item_brand: card.dataset.lyraItemBrand,
      item_category: card.dataset.lyraItemCategory,
      item_variant: card.dataset.lyraItemVariant,
      item_list_id: itemListId,
      item_list_name: itemListName,
      index: cardIndex(card),
      price: numberFromMinorUnits(card.dataset.lyraPriceMinorUnits),
      quantity: 1,
      shopify_product_id: shopifyProductId,
      shopify_variant_id: shopifyVariantId,
      sku: card.dataset.lyraSku,
      base_product_id: card.dataset.lyraBaseProductId,
      colour: card.dataset.lyraColour,
      size: card.dataset.lyraSize
    });
  }

  function storeListSelection(item) {
    try {
      window.sessionStorage.setItem('lyra:last_item_selection', JSON.stringify({
        created_at: Date.now(),
        item
      }));
    } catch (error) {
      // Storage can be unavailable in private browsing. Tracking remains passive.
    }
  }

  function readListSelection(shopifyProductId) {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem('lyra:last_item_selection') || 'null');
      const isRecent = stored && Date.now() - stored.created_at < 30 * 60 * 1000;

      if (isRecent && String(stored.item?.shopify_product_id) === String(shopifyProductId)) {
        return stored.item;
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  function flushImpressions() {
    impressionFlushTimer = null;

    queuedImpressions.forEach((items, groupKey) => {
      if (!items.length) return;

      const firstItem = items[0];
      push('view_item_list', {
        collection: currentCollection(),
        visibility: {
          minimum_ratio: IMPRESSION_RATIO,
          minimum_duration_ms: IMPRESSION_DURATION_MS,
          list_signature: groupKey
        },
        ecommerce: {
          currency: context.page.currency,
          items
        }
      });
    });

    queuedImpressions.clear();
  }

  function queueImpression(card) {
    const item = itemFromCard(card);
    const signature = currentListSignature();
    const key = [
      signature,
      item.item_list_id,
      item.shopify_product_id,
      item.shopify_variant_id,
      item.index
    ].join('|');

    if (seenImpressions.has(key)) return;
    seenImpressions.add(key);

    if (!queuedImpressions.has(signature)) queuedImpressions.set(signature, []);
    queuedImpressions.get(signature).push(item);

    if (!impressionFlushTimer) impressionFlushTimer = window.setTimeout(flushImpressions, 100);
  }

  const cardObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          cardRatios.set(card, entry.intersectionRatio);

          if (entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_RATIO) {
            if (impressionTimers.has(card)) return;

            const timer = window.setTimeout(() => {
              impressionTimers.delete(card);

              if (
                document.visibilityState === 'visible' &&
                document.documentElement.contains(card) &&
                (cardRatios.get(card) || 0) >= IMPRESSION_RATIO
              ) {
                queueImpression(card);
              }
            }, IMPRESSION_DURATION_MS);

            impressionTimers.set(card, timer);
          } else if (impressionTimers.has(card)) {
            window.clearTimeout(impressionTimers.get(card));
            impressionTimers.delete(card);
          }
        });
      }, { threshold: [0, IMPRESSION_RATIO, 1] })
    : null;

  function ctaSource(button) {
    return button?.closest('[data-lyra-cta-source]')?.dataset.lyraCtaSource || 'unknown';
  }

  function isPdpCtaSource(source) {
    return source === 'main_pdp' || source === 'sticky_pdp';
  }

  // IntersectionObserver reports opacity:0 / visibility:hidden boxes as intersecting.
  // The sticky bar sits in the DOM like that until it reveals on scroll, so a CTA
  // view must also confirm the button is actually painted.
  function isPaintVisible(element) {
    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }

    for (let node = element; node; node = node.parentElement) {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    }

    return true;
  }

  function formCtaSource(form) {
    if (form?.dataset?.lyraCtaSource) return form.dataset.lyraCtaSource;
    if (!form?.id) return 'unknown';

    const controls = Array.from(document.querySelectorAll(`[data-lyra-cta-source][form="${CSS.escape(form.id)}"]`));
    return controls.map((control) => control.dataset.lyraCtaSource).find(isPdpCtaSource) || 'unknown';
  }

  function buttonState(button) {
    const explicitState = button?.closest('[data-lyra-button-state]')?.dataset.lyraButtonState;
    if (explicitState) return explicitState;
    if (button?.disabled) return 'sold_out';
    if (button?.closest('buy-buttons')?.getAttribute('template')?.includes('pre-order')) return 'pre_order';
    return 'add_to_cart';
  }

  function variantById(variantId) {
    return context.product?.variants?.find((variant) => String(variant.shopify_variant_id) === String(variantId));
  }

  function selectedVariant(form) {
    return variantById(form?.elements?.id?.value || context.product?.selected_variant_id);
  }

  function optionValues(variant) {
    const result = {};

    context.product?.options?.forEach((option, index) => {
      if (option.kind === 'colour') result.colour = variant?.options?.[index];
      if (option.kind === 'size') result.size = variant?.options?.[index];
    });

    return result;
  }

  function itemFromVariant(variant, quantity = 1) {
    if (!context.product) return {};

    const origin = readListSelection(context.product.shopify_product_id);
    const shopifyProductId = context.product.shopify_product_id;
    const shopifyVariantId = variant?.shopify_variant_id;

    return cleanObject({
      item_id: canonicalItemId(shopifyProductId, shopifyVariantId),
      item_name: context.product.name,
      item_brand: context.product.brand,
      item_category: context.product.category,
      item_variant: variant?.name,
      item_list_id: origin?.item_list_id,
      item_list_name: origin?.item_list_name,
      index: origin?.index,
      price: numberFromMinorUnits(variant?.price_minor_units),
      quantity,
      shopify_product_id: shopifyProductId,
      shopify_variant_id: shopifyVariantId,
      sku: variant?.sku,
      base_product_id: context.product.base_product_id,
      ...optionValues(variant)
    });
  }

  const ctaObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const button = entry.target;
          ctaRatios.set(button, entry.intersectionRatio);

          if (entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_RATIO) {
            if (ctaTimers.has(button)) return;

            const timer = window.setTimeout(() => {
              ctaTimers.delete(button);

              if (
                document.visibilityState !== 'visible' ||
                !document.documentElement.contains(button) ||
                (ctaRatios.get(button) || 0) < IMPRESSION_RATIO ||
                !isPaintVisible(button)
              ) return;

              const form = button.form;
              const variant = selectedVariant(form);
              const source = ctaSource(button);
              const state = buttonState(button);

              push('pdp_cta_view', {
                interaction: {
                  cta_source: source,
                  button_state: state,
                  minimum_ratio: IMPRESSION_RATIO,
                  minimum_duration_ms: IMPRESSION_DURATION_MS
                },
                ecommerce: {
                  currency: context.page.currency,
                  items: [itemFromVariant(variant)]
                }
              }, { dedupeKey: `cta-view:${source}:${state}:${variant?.shopify_variant_id}` });
            }, IMPRESSION_DURATION_MS);

            ctaTimers.set(button, timer);
          } else if (ctaTimers.has(button)) {
            window.clearTimeout(ctaTimers.get(button));
            ctaTimers.delete(button);
          }
        });
      }, { threshold: [0, IMPRESSION_RATIO, 1] })
    : null;

  function observeCards(root = document) {
    if (context.page.type !== 'collection') return;

    root.querySelectorAll?.(COLLECTION_CARD_SELECTOR).forEach((card) => {
      if (observedCards.has(card)) return;
      observedCards.add(card);

      if (cardObserver) cardObserver.observe(card);
    });
  }

  function observeCtas(root = document) {
    if (context.page.type !== 'product') return;

    root.querySelectorAll?.(CTA_CONTROL_SELECTOR).forEach((button) => {
      if (!isPdpCtaSource(ctaSource(button)) || button.closest('.shopify-payment-button')) return;
      if (observedCtas.has(button)) return;
      observedCtas.add(button);
      if (ctaObserver) ctaObserver.observe(button);
    });
  }

  function errorCategory(message) {
    const normalized = String(message || '').toLowerCase();
    if (normalized.includes('sold out')) return 'sold_out';
    if (normalized.includes('inventory') || normalized.includes('available')) return 'inventory_unavailable';
    if (normalized.includes('maximum') || normalized.includes('quantity')) return 'quantity_limit';
    return 'unknown';
  }

  document.addEventListener('click', (event) => {
    const cardLink = event.target.closest?.(`${COLLECTION_CARD_SELECTOR} a[href*="/products/"]`);

    if (cardLink && context.page.type === 'collection') {
      const card = cardLink.closest('[data-lyra-product-card]');
      const item = itemFromCard(card);
      storeListSelection(item);

      push('select_item', {
        collection: context.collection,
        interaction: {
          link_type: cardLink.classList.contains('product-card__media') ? 'image' : 'title'
        },
        ecommerce: {
          currency: context.page.currency,
          items: [item]
        }
      });
    }

    if (context.page.type !== 'product') return;

    const submitButton = event.target.closest?.(CTA_CONTROL_SELECTOR);

    if (
      submitButton &&
      isPdpCtaSource(ctaSource(submitButton)) &&
      !submitButton.closest('.shopify-payment-button')
    ) {
      const variant = selectedVariant(submitButton.form);
      push('pdp_cta_click', {
        interaction: {
          cta_source: ctaSource(submitButton),
          button_state: buttonState(submitButton)
        },
        ecommerce: {
          currency: context.page.currency,
          items: [itemFromVariant(variant)]
        }
      });
    }

    const sizeGuideTrigger = event.target.closest?.(
      '[data-size-wizard-open], .size-wizard-trigger, button[aria-controls^="size-chart-"]'
    );

    if (sizeGuideTrigger) {
      push('pdp_size_guide_open', {
        interaction: {
          entry_point: sizeGuideTrigger.matches('[data-size-wizard-open], .size-wizard-trigger')
            ? 'size_wizard'
            : 'size_chart'
        }
      });
    }

    const summary = event.target.closest?.('.product-info summary, #product-extra-information summary');
    const details = summary?.closest('details[data-lyra-pdp-content-key]');

    if (details && !details.open) {
      const contentKey = details.dataset.lyraPdpContentKey;
      const eventName = contentKey === 'delivery_returns'
        ? 'pdp_delivery_returns_open'
        : contentKey === 'reviews'
          ? 'pdp_reviews_open'
          : 'pdp_product_details_open';

      push(eventName, { interaction: { content_key: contentKey } });
    }
  }, true);

  document.addEventListener('submit', (event) => {
    if (context.page.type !== 'product' || !event.target.matches('form[is="product-form"]')) return;

    const form = event.target;
    if (!form.checkValidity()) return;

    const button = event.submitter || document.activeElement;
    const variant = selectedVariant(form);
    const source = ctaSource(button);
    if (!isPdpCtaSource(source) || button?.closest?.('.shopify-payment-button')) return;

    const state = buttonState(button);
    const quantity = Number(form.elements.quantity?.value || 1);
    const attemptId = createId();

    pendingAttempts.set(form, { attemptId, source, state, quantity, variant });

    push('add_to_cart_attempt', {
      interaction: {
        attempt_id: attemptId,
        cta_source: source,
        button_state: state
      },
      ecommerce: {
        currency: context.page.currency,
        value: totalValue(variant?.price_minor_units, quantity),
        items: [itemFromVariant(variant, quantity)]
      }
    });
  }, true);

  document.addEventListener('variant:add', (event) => {
    if (context.page.type !== 'product') return;

    const form = event.target;
    const attempt = pendingAttempts.get(form);
    if (!attempt) return;

    const responseItem = event.detail?.items?.[0];
    const variant = variantById(responseItem?.variant_id || attempt.variant?.shopify_variant_id);
    const quantity = Number(responseItem?.quantity || attempt.quantity || 1);

    push('add_to_cart', {
      interaction: {
        attempt_id: attempt.attemptId,
        cta_source: attempt.source || 'unknown',
        button_state: attempt.state || 'add_to_cart'
      },
      ecommerce: {
        currency: context.page.currency,
        value: totalValue(variant?.price_minor_units, quantity),
        items: [itemFromVariant(variant, quantity)]
      }
    }, { dedupeKey: `add-to-cart:${attempt.attemptId || createId()}` });

    pendingAttempts.delete(form);
  });

  document.addEventListener('cart:error', (event) => {
    if (context.page.type !== 'product') return;

    const form = event.target;
    const attempt = pendingAttempts.get(form);
    if (!attempt) return;

    push('add_to_cart_error', {
      interaction: {
        attempt_id: attempt.attemptId,
        cta_source: attempt.source || 'unknown',
        button_state: attempt.state || 'add_to_cart',
        error_category: errorCategory(event.detail?.error)
      },
      ecommerce: {
        currency: context.page.currency,
        items: [itemFromVariant(attempt.variant, attempt.quantity)]
      }
    });

    pendingAttempts.delete(form);
  });

  document.addEventListener('variant:change', (event) => {
    if (context.page.type !== 'product' || !context.product || !isPdpCtaSource(formCtaSource(event.target))) return;

    const { variant: eventVariant, previousVariant: eventPreviousVariant } = event.detail || {};
    if (!eventVariant || !eventPreviousVariant) return;

    // The theme's variant:change event uses Shopify's native variant shape
    // (`id`, `price`, `title`). Resolve those objects back to the analytics
    // context so item payloads retain the canonical identifiers and pricing.
    const variant = variantById(eventVariant.shopify_variant_id || eventVariant.id) || eventVariant;
    const previousVariant = variantById(
      eventPreviousVariant.shopify_variant_id || eventPreviousVariant.id
    ) || eventPreviousVariant;

    context.product.options.forEach((option, index) => {
      const value = variant.options?.[index];
      const previousValue = previousVariant.options?.[index];
      if (value === previousValue || !['size', 'colour'].includes(option.kind)) return;

      const unavailableSize = option.kind === 'size' && variant.available === false;
      const eventName = unavailableSize
        ? 'pdp_unavailable_size_select'
        : option.kind === 'size'
          ? 'pdp_size_select'
          : 'pdp_colour_select';

      push(eventName, {
        interaction: {
          option_name: option.name,
          option_value: value,
          previous_value: previousValue,
          availability_state: variant.available ? 'available' : 'unavailable'
        },
        ecommerce: {
          currency: context.page.currency,
          items: [itemFromVariant(variant)]
        }
      });
    });
  });

  document.addEventListener('carousel:change', (event) => {
    if (context.page.type !== 'product' || !event.target.closest?.('product-gallery')) return;

    const cell = event.detail?.cell;
    push('pdp_gallery_interaction', {
      interaction: {
        interaction_type: 'slide_change',
        media_position: Number(event.detail?.index) + 1,
        media_type: cell?.dataset?.mediaType,
        media_id: cell?.dataset?.mediaId
      }
    });
  });

  document.addEventListener('lightbox:open', (event) => {
    if (context.page.type !== 'product' || !event.target.closest?.('product-gallery')) return;

    push('pdp_gallery_interaction', {
      interaction: {
        interaction_type: 'lightbox_open',
        media_position: Number(event.detail?.index ?? 0) + 1
      }
    });
  });

  document.addEventListener('media:play', (event) => {
    if (context.page.type !== 'product' || !event.target.closest?.('product-gallery')) return;

    const media = event.target.closest('[data-media-id]');
    push('pdp_video_play', {
      interaction: {
        media_id: media?.dataset?.mediaId,
        media_type: media?.dataset?.mediaType,
        media_position: Number(media?.dataset?.no || media?.dataset?.mediaPosition || 0) + 1
      }
    }, { dedupeKey: `video-play:${media?.dataset?.mediaId || 'unknown'}` });
  });

  const mutationObserver = new MutationObserver((mutations) => {
    let collectionSurfaceReplaced = false;

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (
          node.matches('[data-lyra-collection-result-count]') ||
          node.querySelector('[data-lyra-collection-result-count]')
        ) collectionSurfaceReplaced = true;
        observeCards(node);
        observeCtas(node);
        watchStickyBars(node);
        if (node.matches('[data-lyra-product-card]')) observeCards(node.closest('.collection__main') || node.parentElement || document);
        if (node.matches('[data-lyra-cta-source]')) observeCtas(node.parentElement || document);
      });
    });

    if (collectionSurfaceReplaced && context.page.type === 'collection' && !facetActionFlushTimer) {
      facetActionFlushTimer = window.setTimeout(flushFacetAction, 0);
    }
  });

  // The sticky bar's CTA never leaves the viewport; it toggles an `is-visible` class
  // instead. Re-observe its buttons on each toggle so the observer re-evaluates them
  // (and the paint gate above decides), otherwise a reveal after load is never seen.
  const stickyBarObserver = new MutationObserver((mutations) => {
    if (!ctaObserver) return;

    mutations.forEach((mutation) => {
      mutation.target.querySelectorAll(CTA_CONTROL_SELECTOR).forEach((button) => {
        if (!observedCtas.has(button)) return;
        if (ctaTimers.has(button)) {
          window.clearTimeout(ctaTimers.get(button));
          ctaTimers.delete(button);
        }
        ctaObserver.unobserve(button);
        ctaObserver.observe(button);
      });
    });
  });

  function watchStickyBars(root = document) {
    if (context.page.type !== 'product') return;

    const bars = root.matches?.('product-sticky-bar') ? [root] : Array.from(root.querySelectorAll?.('product-sticky-bar') || []);
    bars.forEach((bar) => stickyBarObserver.observe(bar, { attributes: true, attributeFilter: ['class'] }));
  }

  observeCards();
  observeCtas();
  watchStickyBars();
  mutationObserver.observe(document.getElementById('main') || document.body, { childList: true, subtree: true });

  window.LyraAnalytics = Object.freeze({
    initialized: true,
    context,
    push,
    debugEnabled
  });
})();
